import _ from 'lodash';
import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';

import { IComposePanelFields } from './compose-panel.types';
import { DRAFT_AUTOSAVE_MS } from './compose-panel.constants';
import { buildInitialValues } from './compose-panel.helpers';
import {
  IStoredDraft,
  createDraftId,
  isDraftNonEmpty,
  loadDrafts,
  removeDraft,
  upsertDraft
} from './compose-panel.drafts';
import {
  setCommunicationDraftCount,
  showCommunicationCompose
} from '../../../lib/stores/communication.store';
import { buildAudienceFromSelections } from '../AudienceSelector/audience-selector.helpers';
import { IComposeContextRef, IComposeContextSelection } from '../../../lib/types';

interface UseComposeDraftsArgs {
  storageKey: string;
  isDraftsView: boolean;
  // Panel open-state + the page-published context (raw refs + the panel's resolved
  // suggestions) drive the open-transition draft pick and its auto-apply.
  isPanelOpen: boolean;
  pageRefs: IComposeContextRef[];
  pageSuggestions: IComposeContextSelection[];
  suggestionsLoading: boolean;
}

// A draft "belongs to" a page context when its contexts include that entity.
const draftMatchesRefs = (draft: IStoredDraft, refs: IComposeContextRef[]): boolean =>
  (draft.values.contexts || []).some(
    (context) => refs.some((ref) => ref.type === context.type && ref.id === context.id)
  );

interface UseComposeDraftsReturn {
  drafts: IStoredDraft[];
  currentDraftId: string;
  currentDraftIdRef: MutableRefObject<string>;
  initialValues: IComposePanelFields;
  persistDraft: ((values: IComposePanelFields) => void) & _.Cancelable;
  showDraftRestoredBanner: boolean;
  dismissDraftBanner: () => void;
  startFreshDraft: () => void;
  handleDiscardDraft: () => void;
  handleNewMessage: () => void;
  handleOpenDraft: (draft: IStoredDraft) => void;
  handleDeleteDraft: (id: string) => void;
  retireDraftAfterSend: () => void;
  resetContent: () => void;
  // True while a fresh-opened-from-a-page draft waits for its context to resolve.
  loadingPageContext: boolean;
}

// HOOK: owns the compose panel's saved-draft concern — the persisted draft set,
// the working draft + autosave, and the "draft restored" banner.
export const useComposeDrafts = ({
  storageKey,
  isDraftsView,
  isPanelOpen,
  pageRefs,
  pageSuggestions,
  suggestionsLoading
}: UseComposeDraftsArgs): UseComposeDraftsReturn => {
  const [showDraftRestoredBanner, setShowDraftRestoredBanner] = useState(false);

  // The full saved-draft set plus the id of the one in the form; on mount the
  // newest draft is restored so an interrupted compose survives a reload.
  const [drafts, setDrafts] = useState<IStoredDraft[]>(() => loadDrafts(storageKey));
  const [currentDraftId, setCurrentDraftId] = useState<string>(() => drafts[0]?.id || createDraftId());
  // Read inside the autosave debounce so a draft switch (open/new) retargets
  // the write without rebuilding the debouncer and dropping a pending save.
  const currentDraftIdRef = useRef(currentDraftId);
  useEffect(() => { currentDraftIdRef.current = currentDraftId; }, [currentDraftId]);

  const [initialValues, setInitialValues] = useState<IComposePanelFields>(() => {
    const restored = drafts[0];
    return restored ? { ...buildInitialValues(), ...restored.values } : buildInitialValues();
  });

  // Surface "Draft restored" only when we actually restored something non-empty.
  useEffect(() => {
    if (drafts[0] && isDraftNonEmpty(drafts[0].values)) setShowDraftRestoredBanner(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Publish the saved-draft count so the header's drafts toggle can badge it.
  useEffect(() => { setCommunicationDraftCount(drafts.length); }, [drafts.length]);

  // storageKey read via ref so the debouncer never rebuilds on an identity
  // change and drops a pending trailing save.
  const storageKeyRef = useRef(storageKey);
  useEffect(() => { storageKeyRef.current = storageKey; }, [storageKey]);

  // Lazy useRef, not useMemo (which React may evict mid-autosave and orphan the
  // trailing save) — same guarantee as use-debounced-search.ts.
  const persistDraftRef = useRef<((values: IComposePanelFields) => void) & _.Cancelable>();
  if (!persistDraftRef.current) {
    persistDraftRef.current = _.debounce((values: IComposePanelFields) => {
      // An empty working draft is never written — "New message" starts empty
      // and shouldn't litter the drafts list until the user types something.
      if (!isDraftNonEmpty(values)) return;
      const draft: IStoredDraft = { id: currentDraftIdRef.current, savedAt: Date.now(), values };
      setDrafts(upsertDraft(storageKeyRef.current, draft));
    }, DRAFT_AUTOSAVE_MS);
  }
  const persistDraft = persistDraftRef.current;

  useEffect(() => () => persistDraft.cancel(), [persistDraft]);

  // Flush any pending autosave when entering the drafts list so the list
  // immediately reflects the message the user was just editing.
  useEffect(() => {
    if (isDraftsView) persistDraft.flush();
  }, [isDraftsView, persistDraft]);

  // On the panel-open transition, pick the draft for the page's context: load a
  // draft already targeting it, else start fresh and await its auto-apply.
  const [awaitingPageContext, setAwaitingPageContext] = useState(false);
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const opened = isPanelOpen && !wasOpenRef.current;
    wasOpenRef.current = isPanelOpen;
    if (!isPanelOpen) { setAwaitingPageContext(false); return; }
    if (!opened || !pageRefs.length) return;

    persistDraft.cancel();
    const match = drafts.find((draft) => draftMatchesRefs(draft, pageRefs));
    if (match) {
      setCurrentDraftId(match.id);
      setInitialValues({ ...buildInitialValues(), ...match.values });
      setShowDraftRestoredBanner(isDraftNonEmpty(match.values));
      setAwaitingPageContext(false);
    } else {
      // Fresh now so no unrelated draft flashes; the context auto-applies on resolve.
      setCurrentDraftId(createDraftId());
      setInitialValues(buildInitialValues());
      setShowDraftRestoredBanner(false);
      setAwaitingPageContext(true);
    }
  }, [isPanelOpen, pageRefs, drafts, persistDraft]);

  // Auto-apply the page context once the panel resolves its label; a cross-assessment
  // ref resolves to nothing, so the fresh draft simply stays empty.
  useEffect(() => {
    if (!awaitingPageContext || suggestionsLoading) return;
    setAwaitingPageContext(false);
    if (!pageSuggestions.length) return;
    setInitialValues({
      ...buildInitialValues(),
      contexts: pageSuggestions,
      audience: buildAudienceFromSelections(pageSuggestions)
    });
  }, [awaitingPageContext, suggestionsLoading, pageSuggestions]);

  const startFreshDraft = useCallback(() => {
    persistDraft.cancel();
    setCurrentDraftId(createDraftId());
    setInitialValues(buildInitialValues());
    setShowDraftRestoredBanner(false);
  }, [persistDraft]);

  const handleDiscardDraft = useCallback(() => {
    setDrafts(removeDraft(storageKey, currentDraftIdRef.current));
    startFreshDraft();
  }, [storageKey, startFreshDraft]);

  const handleNewMessage = useCallback(() => {
    startFreshDraft();
    showCommunicationCompose();
  }, [startFreshDraft]);

  const handleOpenDraft = useCallback((draft: IStoredDraft) => {
    persistDraft.cancel();
    setCurrentDraftId(draft.id);
    setInitialValues({ ...buildInitialValues(), ...draft.values });
    setShowDraftRestoredBanner(false);
    showCommunicationCompose();
  }, [persistDraft]);

  const handleDeleteDraft = useCallback((id: string) => {
    setDrafts(removeDraft(storageKey, id));
    // Deleting the draft currently in the form drops the user onto a fresh one.
    if (id === currentDraftIdRef.current) startFreshDraft();
  }, [storageKey, startFreshDraft]);

  const dismissDraftBanner = useCallback(() => setShowDraftRestoredBanner(false), []);

  // A successful send retires the working draft and starts a fresh one so the
  // next compose doesn't reopen the message that just went out.
  const retireDraftAfterSend = useCallback(() => {
    persistDraft.cancel();
    setDrafts(removeDraft(storageKey, currentDraftIdRef.current));
    setCurrentDraftId(createDraftId());
  }, [persistDraft, storageKey]);

  // Reseed the form with a fresh empty document ("Compose another"); the
  // working-draft id and persisted set are untouched.
  const resetContent = useCallback(() => {
    setInitialValues(buildInitialValues());
  }, []);

  return {
    drafts,
    currentDraftId,
    currentDraftIdRef,
    initialValues,
    persistDraft,
    showDraftRestoredBanner,
    dismissDraftBanner,
    startFreshDraft,
    handleDiscardDraft,
    handleNewMessage,
    handleOpenDraft,
    handleDeleteDraft,
    retireDraftAfterSend,
    resetContent,
    loadingPageContext: awaitingPageContext
  };
};
